/* =====================================================================
   Collection Quick Add
   - Inline variant picker for product cards (color swatches + option pills)
   - Resolves selection to a variant id, updates the add-to-cart button,
     live price and sold-out state, and marks unreachable combos.
   - Adds loading / success / error visual feedback + toast on errors
     for AJAX add-to-cart buttons used by the collection grid and the
     featured products section.
   ===================================================================== */

(function () {
  'use strict';

  if (window.__collectionQuickAddInit) return;
  window.__collectionQuickAddInit = true;

  // Buttons across collection-style sections that perform AJAX add-to-cart.
  var ATC_SELECTOR =
    '[data-main-collection-grid-add-to-cart], [data-featured-products-add-to-cart]';

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.money_format);
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function getVariants(optionsWrap) {
    var tag = optionsWrap.querySelector('.collection-variants-data');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return [];
    }
  }

  function getSelection(optionsWrap) {
    var selection = [null, null, null];
    optionsWrap.querySelectorAll('[data-option-position]').forEach(function (group) {
      var pos = parseInt(group.dataset.optionPosition, 10);
      if (!pos) return;
      // Pills / swatches: read the currently active button.
      var active = group.querySelector('button.is-active[data-option-value]');
      if (active) {
        selection[pos - 1] = active.dataset.optionValue;
        return;
      }
      // Dropdown: read the <select>'s current value.
      var sel = group.querySelector('select.collection-option-select');
      if (sel) selection[pos - 1] = sel.value;
    });
    return selection;
  }

  function findVariant(variants, selection) {
    return variants.find(function (v) {
      return (
        (selection[0] == null || v.option1 === selection[0]) &&
        (selection[1] == null || v.option2 === selection[1]) &&
        (selection[2] == null || v.option3 === selection[2])
      );
    });
  }

  // For each option value, test whether ANY available variant exists that
  // matches the current selection if we swap THIS option to that value.
  //   - Pills / swatches → toggle `.is-unavailable` class
  //   - <option>s inside dropdown → toggle `disabled` + append " (sold out)"
  function markUnavailable(optionsWrap, variants, selection) {
    optionsWrap.querySelectorAll('[data-option-position]').forEach(function (group) {
      var pos = parseInt(group.dataset.optionPosition, 10);
      if (!pos) return;

      // Pills and color swatches.
      group.querySelectorAll('button[data-option-value]').forEach(function (btn) {
        var testSel = selection.slice();
        testSel[pos - 1] = btn.dataset.optionValue;
        var match = findVariant(variants, testSel);
        if (!match || !match.available) {
          btn.classList.add('is-unavailable');
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.classList.remove('is-unavailable');
          btn.removeAttribute('aria-disabled');
        }
      });

      // Dropdown <option> elements.
      var sel = group.querySelector('select.collection-option-select');
      if (sel) {
        Array.from(sel.options).forEach(function (opt) {
          if (!opt.dataset.qaOrig) opt.dataset.qaOrig = opt.textContent;
          var testSel = selection.slice();
          testSel[pos - 1] = opt.value;
          var match = findVariant(variants, testSel);
          if (!match) {
            opt.disabled = true;
            opt.textContent = opt.dataset.qaOrig + ' \u2014 unavailable';
          } else if (!match.available) {
            opt.disabled = false;
            opt.textContent = opt.dataset.qaOrig + ' \u2014 sold out';
          } else {
            opt.disabled = false;
            opt.textContent = opt.dataset.qaOrig;
          }
        });
      }
    });
  }

  function getStrings() {
    var s = window.variantStrings || {};
    return {
      addToCart: s.addToCart || 'Add to cart',
      soldOut: s.soldOut || 'Sold out',
      unavailable: s.unavailable || 'Unavailable',
    };
  }

  // Find the first non-empty text node directly inside the button so we
  // can rewrite the label without disturbing child icons (<i>, <svg>, ...).
  function findLabelTextNode(el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      var node = el.childNodes[i];
      if (node.nodeType === 3 && node.nodeValue && node.nodeValue.trim()) {
        return node;
      }
    }
    return null;
  }

  function setButtonText(btn, text) {
    var node = findLabelTextNode(btn);
    if (node) {
      node.nodeValue = ' ' + text + ' ';
    } else {
      // Fallback: prepend a text node so the label is still updatable later.
      btn.insertBefore(document.createTextNode(' ' + text + ' '), btn.firstChild);
    }
  }

  function snapshotDefaultText(btn) {
    if (btn.dataset.qaDefaultText) return;
    var node = findLabelTextNode(btn);
    btn.dataset.qaDefaultText = node ? node.nodeValue.trim() : getStrings().addToCart;
  }

  // Different sections use different sold-out classes:
  //   main-collection-product-grid -> .btn--soldout
  //   featured-products            -> .is-soldout
  // Add/remove both so visual styles apply regardless of host section.
  var SOLDOUT_CLASSES = ['btn--soldout', 'is-soldout'];

  function applyAtcState(btn, variant) {
    snapshotDefaultText(btn);
    var strings = getStrings();
    if (!variant) {
      btn.dataset.variantId = '';
      SOLDOUT_CLASSES.forEach(function (c) { btn.classList.add(c); });
      btn.setAttribute('aria-disabled', 'true');
      setButtonText(btn, strings.unavailable);
    } else if (!variant.available) {
      btn.dataset.variantId = variant.id;
      SOLDOUT_CLASSES.forEach(function (c) { btn.classList.add(c); });
      btn.setAttribute('aria-disabled', 'true');
      setButtonText(btn, strings.soldOut);
    } else {
      btn.dataset.variantId = variant.id;
      SOLDOUT_CLASSES.forEach(function (c) { btn.classList.remove(c); });
      btn.removeAttribute('aria-disabled');
      setButtonText(btn, btn.dataset.qaDefaultText || strings.addToCart);
    }
  }

  function getVariantImage(optionsWrap, variant) {
    if (variant && variant.image) return variant.image;

    var activeSwatch = optionsWrap.querySelector(
      '.collection-color-swatches .collection-swatch-dot.is-active[data-variant-image]'
    );
    if (activeSwatch && activeSwatch.dataset.variantImage) {
      return activeSwatch.dataset.variantImage;
    }

    return '';
  }

  function updateCard(optionsWrap, variant) {
    var card = optionsWrap.closest('.product-item');
    if (!card) return;

    // Sync add-to-cart buttons inside this card with the new variant.
    card.querySelectorAll(ATC_SELECTOR).forEach(function (btn) {
      applyAtcState(btn, variant);
    });

    if (!variant) return;

    // Live price update.
    var priceEl = card.querySelector('.product-price');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);

    var compareEl = card.querySelector('.product-compare-price');
    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Keep the card image in sync with the selected variant.
    var nextImage = getVariantImage(optionsWrap, variant);
    var primaryImage = card.querySelector('.primary-image');
    if (primaryImage && nextImage) {
      if (primaryImage.currentSrc !== nextImage && primaryImage.src !== nextImage) {
        primaryImage.style.opacity = '0';
        primaryImage.src = nextImage;
        primaryImage.removeAttribute('srcset');
        primaryImage.style.opacity = '1';
      }
    }
  }

  // ------------------------------------------------------------------
  // Option click handler (pills + swatches)
  // ------------------------------------------------------------------

  // Pills / swatches.
  // CSS sets `pointer-events: none` on `.is-unavailable`, so sold-out /
  // unavailable values can't be selected. Only valid combos reach here.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('button[data-option-value]');
    if (!btn) return;

    var optionsWrap = btn.closest('.collection-options');
    if (!optionsWrap) return;

    var group = btn.closest('[data-option-position]');
    if (!group) return;

    // Stop swatch anchor href / other delegated listeners from interfering.
    event.preventDefault();

    // Mark this group's selection.
    group.querySelectorAll('button[data-option-value]').forEach(function (el) {
      el.classList.remove('is-active');
    });
    btn.classList.add('is-active');

    var variants = getVariants(optionsWrap);
    var selection = getSelection(optionsWrap);
    var variant = findVariant(variants, selection);

    updateCard(optionsWrap, variant);
    markUnavailable(optionsWrap, variants, selection);
  });

  // Dropdown selects.
  document.addEventListener('change', function (event) {
    var sel = event.target.closest('select.collection-option-select');
    if (!sel) return;
    var optionsWrap = sel.closest('.collection-options');
    if (!optionsWrap) return;

    var variants = getVariants(optionsWrap);
    var selection = getSelection(optionsWrap);
    var variant = findVariant(variants, selection);

    updateCard(optionsWrap, variant);
    markUnavailable(optionsWrap, variants, selection);
  });

  // ------------------------------------------------------------------
  // Initial pass: mark unreachable combos on first render
  // ------------------------------------------------------------------

  function initAll() {
    document.querySelectorAll('.collection-options').forEach(function (wrap) {
      var variants = getVariants(wrap);
      if (!variants.length) return;
      var selection = getSelection(wrap);
      markUnavailable(wrap, variants, selection);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-initialize after AJAX section re-renders (theme editor + facets/load-more).
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('collection:rendered', initAll);

  // Self-heal: watch the DOM for new .collection-options that appear after
  // facet filters or "load more" replace cards via outerHTML.
  if ('MutationObserver' in window) {
    var pending = false;
    var observer = new MutationObserver(function (mutations) {
      if (pending) return;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (
            n.classList && n.classList.contains('collection-options')
          ) {
            pending = true;
            requestAnimationFrame(function () {
              pending = false;
              initAll();
            });
            return;
          }
          if (n.querySelector && n.querySelector('.collection-options')) {
            pending = true;
            requestAnimationFrame(function () {
              pending = false;
              initAll();
            });
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ------------------------------------------------------------------
  // Add-to-cart visual feedback (loading / success / error)
  // ------------------------------------------------------------------

  function showToast(message, type) {
    var t = document.createElement('div');
    t.className = 'collection-toast collection-toast--' + (type || 'info');
    t.setAttribute('role', type === 'error' ? 'alert' : 'status');
    t.textContent = message;
    document.body.appendChild(t);
    requestAnimationFrame(function () {
      t.classList.add('is-visible');
    });
    setTimeout(function () {
      t.classList.remove('is-visible');
      setTimeout(function () {
        if (t.parentNode) t.parentNode.removeChild(t);
      }, 300);
    }, 2400);
  }

  function setLoading(btn) {
    btn.dataset.qaState = 'loading';
    btn.classList.remove('is-success', 'is-error');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    // Mark this button as initiated by the quick-add flow so global
    // cart:updated events don't accidentally flip unrelated buttons.
    try { btn.dataset.qaQuickAdd = '1'; } catch (e) {}
  }

  function clearState(btn) {
    btn.classList.remove('is-loading', 'is-success', 'is-error');
    btn.removeAttribute('aria-busy');
    delete btn.dataset.qaState;
    try { delete btn.dataset.qaQuickAdd; } catch (e) {}
  }

  function setSuccess(btn) {
    btn.classList.remove('is-loading');
    btn.classList.add('is-success');
    btn.setAttribute('aria-busy', 'false');
    btn.dataset.qaState = 'success';
    setTimeout(function () {
      clearState(btn);
    }, 1400);
  }

  function setError(btn) {
    btn.classList.remove('is-loading');
    btn.classList.add('is-error');
    btn.setAttribute('aria-busy', 'false');
    btn.dataset.qaState = 'error';
    setTimeout(function () {
      clearState(btn);
    }, 1500);
  }

  // Capture-phase click: show spinner BEFORE the section's own handler fires.
  // Also blocks add-to-cart entirely when the current variant is sold out /
  // unavailable so the section's bubble-phase handler doesn't hit the API.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest(ATC_SELECTOR);
      if (!btn) return;

      // NOTE: don't check `aria-disabled` here — the section's existing
      // bubble handler synchronously sets it to "true" before awaiting
      // `addItem`, and capture-phase listeners on the same element continue
      // to run after it (so we'd see a stale `true` on every legitimate
      // click and incorrectly show a "Sold out" toast). The presence of
      // `btn--soldout` / `is-soldout` and an empty `data-variant-id`
      // are the authoritative signals for "not buyable".
      var hasSoldOut =
        btn.classList.contains('btn--soldout') ||
        btn.classList.contains('is-soldout');
      var hasNoVariant = !btn.dataset.variantId;

      if (hasSoldOut || hasNoVariant) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var strings = getStrings();
        var msg = hasSoldOut
          ? strings.soldOut
          : strings.unavailable;
        showToast(msg, 'error');
        return;
      }

      if (btn.dataset.qaState === 'loading') return;
      setLoading(btn);

      // Safety net: if nothing resolves the state within 6s, treat as error
      // so the button doesn't get stuck in the spinner.
      setTimeout(function () {
        if (btn.dataset.qaState === 'loading') {
          setError(btn);
          showToast('Could not add to cart. Please try again.', 'error');
        }
      }, 6000);
    },
    true
  );

  // VanillaCartState dispatches `cart:updated` after a successful add.
  document.addEventListener('cart:updated', function () {
    document
      .querySelectorAll(ATC_SELECTOR + '.is-loading')
      .forEach(function (btn) {
        if (btn.dataset && btn.dataset.qaQuickAdd === '1') {
          setSuccess(btn);
        }
      });
  });

  // Hook into VanillaCartState.addItem to surface real error messages.
  function patchVanillaCart() {
    var vcs = window.VanillaCartState;
    if (!vcs || typeof vcs.addItem !== 'function' || vcs.__quickAddPatched) {
      return;
    }
    var orig = vcs.addItem.bind(vcs);
    vcs.addItem = function () {
      var args = arguments;
      return orig.apply(null, args).catch(function (err) {
        document
          .querySelectorAll(ATC_SELECTOR + '.is-loading')
          .forEach(function (btn) {
            if (btn.dataset && btn.dataset.qaQuickAdd === '1') {
              setError(btn);
            }
          });
        var message =
          (err && err.message) ||
          'This item could not be added to your cart.';
        showToast(message, 'error');
        throw err;
      });
    };
    vcs.__quickAddPatched = true;
  }

  // vanilla-cart-state.js is loaded with `defer` so it may not be ready yet.
  // Try now, after DOMContentLoaded, and once more on next macrotask.
  patchVanillaCart();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchVanillaCart);
  }
  setTimeout(patchVanillaCart, 0);
  setTimeout(patchVanillaCart, 500);
})();
